/**
 * ParcelPickerMap — fullscreen modal that lets the user pick the right
 * cadastral parcel (or several) from a Leaflet map.
 *
 * Tiles : IGN Géoplateforme (gratuit, no-key, CORS ouvert).
 *   - Satellite : ORTHOIMAGERY.ORTHOPHOTOS (orthophoto)
 *   - Plan      : GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2 (carte topo IGN)
 * On bascule l'un ou l'autre via un toggle en haut à droite — utile quand
 * l'orthophoto est trop dense pour identifier la bonne parcelle (zone
 * boisée, ombre, parcelle très petite).
 *
 * Selection semantics :
 *   - clic = single-select (remplace la sélection)
 *   - shift/cmd/ctrl + clic = toggle en multi-select
 *   - au moins une parcelle reste toujours sélectionnée
 *
 * Pourquoi Leaflet directement (pas React-Leaflet) :
 *   - Une seule instance de map dans toute l'app — l'overhead du wrapper
 *     React n'apporte rien et ajoute ~30 Ko + une couche d'indirection.
 *   - Refs Leaflet directs : plus simples à debugger, pas de cycle de vie
 *     React vs. Leaflet à arbitrer.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button, Card } from "./ui";
import { Ic } from "./icons";
import type { CadastreParcel } from "../lib/types";

// IGN WMTS — pattern partagé pour les deux couches. Le {style} et {format}
// changent selon la couche. Les capabilities WMTS de l'IGN sont à
// data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetCapabilities.
const IGN_WMTS_BASE = "https://data.geopf.fr/wmts";
const IGN_TILE_URL = (layer: string, format: "image/jpeg" | "image/png") =>
  `${IGN_WMTS_BASE}?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=${layer}&STYLE=normal&FORMAT=${format}&TILEMATRIXSET=PM&TILEMATRIX={z}&TILECOL={x}&TILEROW={y}`;

// Min/max zoom — les couches IGN couvrent en pratique 0–19. À 18 on voit
// déjà nettement les limites parcellaires sur l'ortho ; on plafonne à 19
// pour éviter le up-scale flou au-delà.
const MIN_ZOOM = 12;
const MAX_ZOOM = 19;

type LayerKey = "satellite" | "plan";

type Props = {
  /** All parcels the user can pick from — includes the auto-picked one. */
  candidates: CadastreParcel[];
  /** IDs currently selected on the fiche. Set (not a single string) so
   *  reopening the modal after a multi-pick restores the highlight on
   *  every component parcel — otherwise the user sees an apparently-empty
   *  map when re-entering to adjust their selection. */
  initialSelectedIds: Set<string>;
  onConfirm: (ids: string[]) => void;
  onCancel: () => void;
};

export function ParcelPickerMap({
  candidates,
  initialSelectedIds,
  onConfirm,
  onCancel,
}: Props) {
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initialSelectedIds),
  );
  const [layerKey, setLayerKey] = useState<LayerKey>("satellite");
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  const layersByIdRef = useRef<Map<string, L.Polygon>>(new Map());
  // Use a ref to expose the latest selection to the click handler — Leaflet
  // event handlers capture their closure once on layer creation; without
  // this, additive (shift+click) would always read the initial set.
  const selectedRef = useRef<Set<string>>(selected);
  selectedRef.current = selected;

  const candidatesById = useMemo(() => {
    const m = new Map<string, CadastreParcel>();
    for (const p of candidates) m.set(p.id, p);
    return m;
  }, [candidates]);

  // ── Init map (once) ──────────────────────────────────────────────────
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    const map = L.map(mapContainerRef.current, {
      zoomControl: true,
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      attributionControl: true,
    });
    mapRef.current = map;

    const tile = L.tileLayer(
      IGN_TILE_URL("ORTHOIMAGERY.ORTHOPHOTOS", "image/jpeg"),
      {
        attribution: "© IGN — Géoplateforme",
        maxZoom: MAX_ZOOM,
        minZoom: MIN_ZOOM,
      },
    ).addTo(map);
    tileLayerRef.current = tile;

    // Build a Leaflet polygon per candidate and stash by id.
    const layers = new Map<string, L.Polygon>();
    for (const p of candidates) {
      const latlngs = polygonToLatLngs(p.geometry);
      if (!latlngs) continue;
      const isSelected = initialSelectedIds.has(p.id);
      const poly = L.polygon(latlngs, styleFor(isSelected));
      poly.bindTooltip(`${p.section}-${p.numero} · ${p.surface_m2} m²`, {
        permanent: true,
        direction: "center",
        className: "parcel-picker-label",
      });
      poly.on("click", (e) => {
        // L.DomEvent.stopPropagation prevents the click from also
        // panning/clicking the map.
        L.DomEvent.stopPropagation(e);
        const orig = e.originalEvent as MouseEvent | undefined;
        const additive = !!(orig && (orig.shiftKey || orig.metaKey || orig.ctrlKey));
        handlePick(p.id, additive);
      });
      poly.addTo(map);
      layers.set(p.id, poly);
    }
    layersByIdRef.current = layers;

    // Fit to bounds of all candidates with a bit of padding so the user
    // sees adjacent context (= the typical 30 m cadastre buffer plus
    // visible neighbors).
    const allBounds = candidates
      .map((p) => layers.get(p.id)?.getBounds())
      .filter((b): b is L.LatLngBounds => !!b);
    if (allBounds.length > 0) {
      const merged = allBounds.reduce(
        (acc, b) => acc.extend(b),
        L.latLngBounds(allBounds[0].getSouthWest(), allBounds[0].getNorthEast()),
      );
      map.fitBounds(merged.pad(0.3), { animate: false });
    }

    return () => {
      map.remove();
      mapRef.current = null;
      tileLayerRef.current = null;
      layersByIdRef.current = new Map();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // candidates considered stable for the lifetime of the modal

  // ── React to selection changes — restyle each polygon ───────────────
  useEffect(() => {
    for (const [id, poly] of layersByIdRef.current.entries()) {
      poly.setStyle(styleFor(selected.has(id)));
    }
  }, [selected]);

  // ── Toggle tile layer ───────────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current || !tileLayerRef.current) return;
    const url =
      layerKey === "satellite"
        ? IGN_TILE_URL("ORTHOIMAGERY.ORTHOPHOTOS", "image/jpeg")
        : IGN_TILE_URL("GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2", "image/png");
    tileLayerRef.current.setUrl(url);
  }, [layerKey]);

  function handlePick(id: string, additive: boolean) {
    setSelected((prev) => {
      if (additive) {
        const next = new Set(prev);
        if (next.has(id)) {
          // Never allow zero-selection — keep at least the clicked one.
          if (next.size > 1) next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      }
      return new Set([id]);
    });
  }

  const selectedParcels = [...selected]
    .map((id) => candidatesById.get(id))
    .filter((p): p is CadastreParcel => !!p);
  const totalSurface = selectedParcels.reduce(
    (s, p) => s + (p.surface_m2 || 0),
    0,
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="parcel-picker-title"
      className="fixed inset-0 z-50 bg-ink-900/60 backdrop-blur-sm overflow-y-auto"
      onClick={onCancel}
    >
      <div className="min-h-full flex items-center justify-center p-4">
        <Card
          className="max-w-[960px] w-full flex flex-col my-4 max-h-[calc(100vh-2rem)]"
          onClick={(e) => e.stopPropagation()}
        >
          <header className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-ink-200">
            <div>
              <h3
                id="parcel-picker-title"
                className="text-[16px] font-semibold tracking-tight"
              >
                Sélectionnez la parcelle du bien
              </h3>
              <p className="mt-0.5 text-[12px] text-ink-500">
                Clic pour choisir ·{" "}
                <kbd className="text-[11px] font-mono">⇧</kbd> ou{" "}
                <kbd className="text-[11px] font-mono">⌘</kbd>+clic pour une
                sélection multi-parcelles · molette pour zoomer
              </p>
            </div>
            <button
              onClick={onCancel}
              className="w-8 h-8 rounded-full hover:bg-ink-100 grid place-items-center text-ink-500"
              aria-label="Fermer"
            >
              <Ic.Cross size={14} />
            </button>
          </header>

          <div className="relative bg-ink-100 overflow-hidden aspect-[3/2] max-h-[calc(100vh-9rem)]">
            <div
              ref={mapContainerRef}
              className="absolute inset-0"
              // Leaflet draws inside this; we keep its z-index below the
              // toggle so the toggle stays clickable.
            />
            <LayerToggle
              value={layerKey}
              onChange={setLayerKey}
            />
          </div>

          <footer className="shrink-0 flex items-center justify-between px-5 py-4 border-t border-ink-200 gap-4">
            <div className="text-[13px] text-ink-700">
              {selectedParcels.length === 1 ? (
                <>
                  Parcelle{" "}
                  <strong className="font-semibold">
                    {selectedParcels[0].section}-{selectedParcels[0].numero}
                  </strong>{" "}
                  · {totalSurface.toLocaleString("fr-FR")} m²
                </>
              ) : (
                <>
                  <strong className="font-semibold">
                    {selectedParcels.length} parcelles
                  </strong>{" "}
                  sélectionnées · {totalSurface.toLocaleString("fr-FR")} m² au
                  total
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="soft" onClick={onCancel}>
                Annuler
              </Button>
              <Button
                variant="dark"
                onClick={() => onConfirm([...selected])}
                iconRight={<Ic.Arrow size={14} />}
              >
                Valider
              </Button>
            </div>
          </footer>
        </Card>
      </div>
    </div>
  );
}

// ─── Layer toggle ────────────────────────────────────────────────────────
function LayerToggle({
  value,
  onChange,
}: {
  value: LayerKey;
  onChange: (v: LayerKey) => void;
}) {
  return (
    <div className="absolute top-3 right-3 z-[1000] flex bg-paper rounded-full hairline shadow-card overflow-hidden text-[12px]">
      <button
        type="button"
        onClick={() => onChange("satellite")}
        className={`px-3 py-1.5 transition ${
          value === "satellite"
            ? "bg-ink-900 text-white"
            : "text-ink-700 hover:bg-ink-50"
        }`}
      >
        Satellite
      </button>
      <button
        type="button"
        onClick={() => onChange("plan")}
        className={`px-3 py-1.5 transition ${
          value === "plan"
            ? "bg-ink-900 text-white"
            : "text-ink-700 hover:bg-ink-50"
        }`}
      >
        Plan
      </button>
    </div>
  );
}

// ─── Geometry helpers ────────────────────────────────────────────────────

type GeoJSONPolygon = {
  type: "Polygon";
  coordinates: number[][][];
};
type GeoJSONMultiPolygon = {
  type: "MultiPolygon";
  coordinates: number[][][][];
};
type AnyGeom = GeoJSONPolygon | GeoJSONMultiPolygon | null | undefined;

/**
 * Convert a (Multi)Polygon to Leaflet's nested LatLng arrays. Note that
 * GeoJSON is [lng, lat] but Leaflet expects [lat, lng] — silently flipping
 * is the most common Leaflet bug, so we do it explicitly here.
 *
 * Returns null when the geometry has no drawable ring.
 */
function polygonToLatLngs(g: unknown): L.LatLngExpression[][][] | null {
  const geom = g as AnyGeom;
  if (!geom) return null;
  if (geom.type === "Polygon") {
    const rings = geom.coordinates;
    if (!rings || rings.length === 0) return null;
    return [rings.map((ring) => ring.map(([lng, lat]) => [lat, lng] as [number, number]))];
  }
  if (geom.type === "MultiPolygon") {
    const polys = geom.coordinates;
    if (!polys || polys.length === 0) return null;
    return polys.map((rings) =>
      rings.map((ring) => ring.map(([lng, lat]) => [lat, lng] as [number, number])),
    );
  }
  return null;
}

function styleFor(isSelected: boolean): L.PathOptions {
  return {
    color: isSelected ? "#14161A" : "rgba(255, 255, 255, 0.95)",
    weight: isSelected ? 2.4 : 1.4,
    fillColor: isSelected ? "#D9EA2C" : "#ffffff",
    fillOpacity: isSelected ? 0.45 : 0.12,
    dashArray: isSelected ? "6 4" : undefined,
  };
}
